import { supabase } from './supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('DUELSTATS');

export interface DuelStats {
  user_id: string;
  wins: number;
  losses: number;
  total_duels: number;
  win_rate_percentage: number;
  win_rate: number;
  last_resolved_at: string | null;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
}

export async function getUserDuelStats(userId: string): Promise<DuelStats | null> {
  const { data, error } = await supabase
    .from('public_user_duel_stats_v1')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    log.error('Error fetching duel stats:', error);
    return null;
  }

  if (!data) {
    return {
      user_id: userId,
      wins: 0,
      losses: 0,
      total_duels: 0,
      win_rate_percentage: 0,
      win_rate: 0,
      last_resolved_at: null,
      username: null,
      display_name: null,
      avatar_url: null,
    };
  }

  return data;
}

export async function getTopDuelists(limit: number = 10): Promise<DuelStats[]> {
  const { data, error } = await supabase
    .from('public_user_duel_stats_v1')
    .select('*')
    .order('wins', { ascending: false })
    .limit(limit);

  if (error) {
    log.error('Error fetching top duelists:', error);
    return [];
  }

  return data || [];
}

export async function getDuelistsByWinRate(
  minDuels: number = 5,
  limit: number = 10
): Promise<DuelStats[]> {
  const { data, error } = await supabase
    .from('public_user_duel_stats_v1')
    .select('*')
    .gte('total_duels', minDuels)
    .order('win_rate', { ascending: false })
    .limit(limit);

  if (error) {
    log.error('Error fetching duelists by win rate:', error);
    return [];
  }

  return data || [];
}

export function formatWinLossRecord(stats: DuelStats): string {
  return `${stats.wins}W - ${stats.losses}L`;
}

export function formatWinRate(stats: DuelStats): string {
  if (stats.total_duels === 0) return '0%';
  return `${stats.win_rate_percentage}%`;
}

export function getDuelRecordColor(stats: DuelStats): string {
  if (stats.total_duels === 0) return '#999';

  const winRate = stats.win_rate;
  if (winRate >= 0.7) return '#4CAF50';
  if (winRate >= 0.5) return '#2196F3';
  if (winRate >= 0.3) return '#FF9800';
  return '#F44336';
}

export function getDuelRecordBadge(stats: DuelStats): string | null {
  if (stats.total_duels === 0) return null;

  if (stats.wins >= 100) return 'Legend';
  if (stats.wins >= 50) return 'Master';
  if (stats.wins >= 25) return 'Expert';
  if (stats.wins >= 10) return 'Veteran';
  if (stats.wins >= 5) return 'Competitor';
  if (stats.total_duels >= 1) return 'Rookie';

  return null;
}
